// src/hooks/useAuth.ts
//
// GIDC (consumer) auth - D-033. Firebase Auth issues the token (central consumer
// pool, D-080); SmartShop's backend verifies it and returns the account.
//
// Two backend calls, deliberately different:
//   smartshop-account-sync  - LOGIN MOMENT only. Writes: binds uid to account,
//                             mints the GID through SmartCom on first registration,
//                             issues the consumer grant. Needs terms_accepted on a
//                             brand-new account (D-079).
//   smartshop-gidc-me       - EVERY OTHER TIME. Pure gated read, no writes.
//
// Replaces useSmartShopAuth, which called account-sync on every auth state change
// (a write on page load) and sent no terms_accepted (so registration 400'd).
//
// House rule: short hyphen "-" only.
import { useState, useEffect, useCallback } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut as fbSignOut,
  onAuthStateChanged,
  User,
} from 'firebase/auth';
import { auth, SMARTSHOP_API, CLIENT_ID } from '../firebaseConfig';

export interface GidcCartItem {
  product_id: string;
  variant_id: string | null;
  qty: number;
  price_snapshot: number;
  name_snapshot: string;
  price_type: 'fixed' | 'variable' | 'enquire';
  variable_amount: number | null;
  image_snapshot?: string | null;
  variant_name_snapshot?: string | null;
  slug?: string | null;
}

export interface GidcAccount {
  account_id: string;
  gid: string;
  email: string;
  name: string | null;
  last_login_at: string | null;
  consents?: Record<string, any>;
  orders: any[];
  saved_cart: {
    cart_id: string;
    item_count: number;
    total: number;
    items: GidcCartItem[];
  } | null;
}

function friendly(code: string): string {
  switch (code) {
    case 'auth/email-already-in-use': return 'That email already has an account. Try signing in.';
    case 'auth/invalid-email':        return 'That email address does not look right.';
    case 'auth/weak-password':        return 'Please choose a password of at least 6 characters.';
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':       return 'Email or password not recognised.';
    case 'auth/too-many-requests':    return 'Too many attempts. Please wait a moment and try again.';
    default:                          return 'Something went wrong. Please try again.';
  }
}

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [account, setAccount] = useState<GidcAccount | null>(null);
  const [checking, setChecking] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadAccount = useCallback(async (u: User) => {
    const token = await u.getIdToken();
    const res = await fetch(`${SMARTSHOP_API}/smartshop-gidc-me`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ gido_id: CLIENT_ID }),
    });
    if (!res.ok) throw new Error(String(res.status));
    const data: GidcAccount = await res.json();
    setAccount(data);
    return data;
  }, []);

  const syncAccount = useCallback(async (u: User, opts: { name?: string; termsAccepted?: boolean }) => {
    const token = await u.getIdToken();
    const res = await fetch(`${SMARTSHOP_API}/smartshop-account-sync`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        gido_id: CLIENT_ID,
        name: opts.name,
        terms_accepted: !!opts.termsAccepted,
        // the guest cart, so the login moment binds it to this account
        // (backend: bind_and_merge_on_login). Null when there is no guest cart.
        cart_id: localStorage.getItem('smartshop_cart_id'),
        session_id: localStorage.getItem('smartshop_session_id'),
      }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || String(res.status));
    }
    const data: GidcAccount = await res.json();
    setAccount(data);
    return data;
  }, []);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      setUser(u);
      if (u) {
        try { await loadAccount(u); } catch { setAccount(null); }
      } else {
        setAccount(null);
      }
      setChecking(false);
    });
    return unsub;
  }, [loadAccount]);

  const register = useCallback(async (email: string, password: string, name: string, termsAccepted: boolean) => {
    setBusy(true); setError(null);
    try {
      if (!termsAccepted) throw new Error('Please accept the terms to create an account.');
      const cred = await createUserWithEmailAndPassword(auth, email.trim(), password);
      await syncAccount(cred.user, { name: name.trim(), termsAccepted: true });
      return true;
    } catch (e: any) {
      setError(e?.code ? friendly(e.code) : (e?.message || 'Could not create the account.'));
      return false;
    } finally { setBusy(false); }
  }, [syncAccount]);

  const signIn = useCallback(async (email: string, password: string) => {
    setBusy(true); setError(null);
    try {
      const cred = await signInWithEmailAndPassword(auth, email.trim(), password);
      await syncAccount(cred.user, {});
      return true;
    } catch (e: any) {
      setError(e?.code ? friendly(e.code) : (e?.message || 'Could not sign in.'));
      return false;
    } finally { setBusy(false); }
  }, [syncAccount]);

  const resetPassword = useCallback(async (email: string) => {
    setBusy(true); setError(null);
    try {
      await sendPasswordResetEmail(auth, email.trim());
      return true;
    } catch (e: any) {
      setError(e?.code ? friendly(e.code) : 'Could not send the reset email.');
      return false;
    } finally { setBusy(false); }
  }, []);

  const signOut = useCallback(async () => {
    await fbSignOut(auth);
    setAccount(null);
  }, []);

  return {
    user, account, checking, busy, error,
    signedIn: !!user,
    register, signIn, signOut, resetPassword,
    refresh: async () => { if (user) return loadAccount(user); },
  };
}